import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  makeStyles,
} from "@material-ui/core";
import ButtonWithLoading from "components/ButtonWithLoading";
import type { MalwareContent, MalwareRecord } from "@dashboard-v3/api";
import { applyMalwareActions } from "../utils";

const FILES_PREVIEW = 10;

interface Props {
  content: MalwareContent;
  onClose: () => void;
  onIgnored: (content: MalwareContent, record: MalwareRecord) => void;
}

export default function IgnoreThreatDialog({
  content,
  onClose,
  onIgnored,
}: Props) {
  const { t } = useTranslation("mxheroShares");
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const [saving, setSaving] = useState(false);
  const files = content.files.slice(0, FILES_PREVIEW);
  const remaining = content.filesCount - files.length;

  async function onIgnoreThreat() {
    setSaving(true);
    try {
      const record = await applyMalwareActions(content.checksum, ["released"]);
      enqueueSnackbar(t("malware.ignore.success"), { variant: "success" });
      onIgnored(content, record);
    } catch (error) {
      enqueueSnackbar(t("malware.errors.ignore"), { variant: "error" });
      setSaving(false);
    }
  }

  function onDialogClose() {
    if (!saving) onClose();
  }

  return (
    <Dialog open onClose={onDialogClose} fullWidth maxWidth="xs">
      <DialogTitle>{t("malware.ignore.title")}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" gutterBottom>
          {t("malware.ignore.explain", { threat: content.threat })}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {t("malware.ignore.filesNotice", { count: content.filesCount })}
        </Typography>
        <ul className={classes.files} data-testid="malware-ignore-files">
          {files.map(file => (
            <li key={file.id}>
              <span>{file.name}</span>
              <span className={classes.user}>{file.user}</span>
            </li>
          ))}
          {remaining > 0 && (
            <li>
              <span className={classes.user}>
                {t("malware.ignore.moreFiles", { count: remaining })}
              </span>
            </li>
          )}
        </ul>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t("common:cancel")}
        </Button>
        <ButtonWithLoading
          color="primary"
          variant="contained"
          disableElevation
          loading={saving}
          data-testid="malware-ignore-confirm"
          onClick={onIgnoreThreat}
        >
          {t("malware.actions.ignoreThreat")}
        </ButtonWithLoading>
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles(theme => ({
  files: {
    listStyle: "none",
    margin: "12px 0 0",
    padding: 0,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    fontSize: "0.85rem",
    "& li": {
      display: "flex",
      justifyContent: "space-between",
      gap: 12,
      padding: "8px 12px",
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    "& li:last-child": { borderBottom: 0 },
  },
  user: { color: theme.palette.text.secondary, whiteSpace: "nowrap" },
}));
